/** Learner/instructor training progress — core 16 skills + focus summary. */
export default function TrainingProgressDashboard({ progression, testIdPrefix = "progress" }) {
  if (!progression) return null;

  const core = progression.core_sixteen_dashboard || [];
  const overall = progression.overall_summary;
  const focus = progression.session_focus;
  const repeated = progression.repeated_weaknesses || [];
  const strengths = progression.strengths_detailed || [];

  if (!core.length && !overall && !focus?.suggested_focus_today?.length) return null;

  return (
    <div className="space-y-4" data-testid={`${testIdPrefix}-dashboard`}>
      {overall && (
        <div className="text-sm space-y-1" data-testid={`${testIdPrefix}-overall`}>
          <p>
            Skills with published scores: {overall.skills_assessed_count ?? 0} · Not yet assessed:{" "}
            {overall.skills_never_assessed_count ?? 0}
          </p>
          {overall.overall_current_mean != null && (
            <p>
              Overall current mean (latest per skill): {overall.overall_current_mean} / 5
              <span className="text-muted-foreground"> — training evidence only</span>
            </p>
          )}
        </div>
      )}

      {focus?.suggested_focus_today?.length > 0 && (
        <div data-testid={`${testIdPrefix}-teach-today`}>
          <h3 className="font-semibold text-sm">Suggested focus today</h3>
          <ul className="text-sm list-disc pl-5">
            {focus.suggested_focus_today.map((label) => (
              <li key={label}>{label}</li>
            ))}
          </ul>
        </div>
      )}

      {focus?.latest_next_session_focus && (
        <div data-testid={`${testIdPrefix}-next-focus`}>
          <h3 className="font-semibold text-sm">Next lesson focus (published)</h3>
          <p className="text-sm">{focus.latest_next_session_focus}</p>
        </div>
      )}

      {repeated.length > 0 && (
        <div data-testid={`${testIdPrefix}-weaknesses`}>
          <h3 className="font-semibold text-sm">Repeated practice areas</h3>
          <ul className="text-sm">
            {repeated.map((w) => (
              <li key={w.task_id}>
                {w.label} — recent low scores ({w.low_count}×)
              </li>
            ))}
          </ul>
        </div>
      )}

      {strengths.length > 0 && (
        <div data-testid={`${testIdPrefix}-strengths`}>
          <h3 className="font-semibold text-sm">Strengths (repeated evidence)</h3>
          <ul className="text-sm">
            {strengths.map((s) => (
              <li key={s.task_id}>
                {s.label} — strong recent scores ({s.high_count}×)
              </li>
            ))}
          </ul>
        </div>
      )}

      {core.length > 0 && (
        <div data-testid={`${testIdPrefix}-core-sixteen`}>
          <h3 className="font-semibold text-sm">Core 16 skills</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b text-left">
                  <th className="py-2 pr-2">Skill</th>
                  <th className="py-2 pr-2">Current</th>
                  <th className="py-2 pr-2">Trend</th>
                  <th className="py-2 pr-2">Coverage</th>
                  <th className="py-2">Last assessed</th>
                </tr>
              </thead>
              <tbody>
                {core.map((item) => (
                  <tr key={item.task_id} className="border-b border-border/50" data-testid={`${testIdPrefix}-skill-${item.task_id}`}>
                    <td className="py-2 pr-2">{item.label}</td>
                    <td className="py-2 pr-2">
                      {item.status === "never_assessed" ? (
                        <span className="text-muted-foreground">Not assessed</span>
                      ) : item.status === "na_only" ? (
                        <span className="text-muted-foreground">N/A only</span>
                      ) : (
                        item.current_score ?? "—"
                      )}
                    </td>
                    <td className="py-2 pr-2">{item.trend}</td>
                    <td className="py-2 pr-2">{item.coverage_count ?? 0}</td>
                    <td className="py-2">{item.last_assessed || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {core.some((i) => (i.score_history || []).length > 0) && (
            <details className="mt-2 text-xs text-muted-foreground">
              <summary>Score history chains</summary>
              <ul className="mt-1 space-y-1">
                {core
                  .filter((i) => (i.score_history || []).length)
                  .map((i) => (
                    <li key={i.task_id}>
                      {i.label}: {(i.score_history || []).join(" → ")}
                    </li>
                  ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </div>
  );
}
